import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface InsightEvent {
  id: string;
  type: 'insight-alert' | 'insight-confirm' | 'insight-var-disallowed';
  time_elapsed: number;
  team_id: string;
  stat_type: 'sot' | 'corners';
  team_name: string;
  /** For alerts: current count and ratio */
  count?: number;
  ratio?: number;
  /** For confirmations: minutes ago */
  minutes_ago?: number;
  is_late?: boolean;
}

function parseInsightLocks(locks: { id: string; metadata: any }[]): InsightEvent[] {
  const events: InsightEvent[] = [];

  for (const lock of locks) {
    const meta = lock.metadata ?? {};

    // Push alert: insight-push-{fixtureId}-{teamId}-{stat}
    if (lock.id.startsWith('insight-push-') && !lock.id.includes('-count-')) {
      const stripped = lock.id.replace('insight-push-', '');
      const parts = stripped.split('-');
      if (parts.length < 3) continue;
      const stat = parts[parts.length - 1] as 'sot' | 'corners';
      if (stat !== 'sot' && stat !== 'corners') continue;
      const teamId = parts.slice(1, -1).join('-');

      events.push({
        id: lock.id,
        type: 'insight-alert',
        time_elapsed: meta.minute ?? 0,
        team_id: teamId,
        stat_type: stat,
        team_name: meta.team_name ?? '?',
        count: meta.count,
        ratio: meta.ratio,
      });
    }

    // Confirm: insight-confirm-{fixtureId}-{teamId}
    if (lock.id.startsWith('insight-confirm-')) {
      const stripped = lock.id.replace('insight-confirm-', '');
      const parts = stripped.split('-');
      if (parts.length < 2) continue;
      const teamId = parts.slice(1).join('-');

      events.push({
        id: lock.id,
        type: 'insight-confirm',
        time_elapsed: meta.goal_minute ?? meta.minutes_ago ?? 0,
        team_id: teamId,
        stat_type: 'sot',
        team_name: meta.team_name ?? '?',
        minutes_ago: meta.minutes_ago,
        is_late: meta.is_late,
      });
    }

    // VAR disallowed: insight-var-disallowed-{fixtureId}-{teamId}
    if (lock.id.startsWith('insight-var-disallowed-')) {
      const stripped = lock.id.replace('insight-var-disallowed-', '');
      const parts = stripped.split('-');
      if (parts.length < 2) continue;
      const teamId = parts.slice(1).join('-');

      events.push({
        id: lock.id,
        type: 'insight-var-disallowed',
        time_elapsed: meta.goal_minute ?? meta.minutes_ago ?? 0,
        team_id: teamId,
        stat_type: 'sot',
        team_name: meta.team_name ?? '?',
        minutes_ago: meta.minutes_ago,
        is_late: meta.is_late,
      });
    }
  }

  return events;
}

export function useInsightEvents(fixtureId: number) {
  return useQuery({
    queryKey: ['insight-events', fixtureId],
    queryFn: async () => {
      const prefix1 = `insight-push-${fixtureId}-`;
      const prefix2 = `insight-confirm-${fixtureId}-`;
      const prefix3 = `insight-var-disallowed-${fixtureId}-`;

      const { data } = await supabase
        .from('sync_control')
        .select('id, metadata')
        .or(`id.like.${prefix1}%,id.like.${prefix2}%,id.like.${prefix3}%`);

      if (!data || data.length === 0) return [];

      // Filter out count locks
      const filtered = data.filter(d => !d.id.includes('-count-'));
      return parseInsightLocks(filtered);
    },
    staleTime: 5 * 60 * 1000,
    enabled: fixtureId > 0,
  });
}
